import React, { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import SecureLS from "secure-ls";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import AccountCircle from "@mui/icons-material/AccountCircle";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import DashboardIcon from "@mui/icons-material/Dashboard";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import Logout from "@mui/icons-material/Logout";
import { fireSwal } from "../../utils/alerts";
import { retailModulesList } from "./retailModules";
import { resolveRetailModulePath } from "./retailModulePaths";

const ls = new SecureLS({ encodingType: "aes" });
const COLLAPSED_KEY = "retailSidebarCollapsed";
const DashboardNotif = lazy(() => import("../../pages/dashboard/Function/DashboardNotif"));
const DashboardInboxModal = lazy(() => import("../../pages/dashboard/Function/DashboardInboxModal"));

const getInitialCollapsed = () => {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(COLLAPSED_KEY) === "true";
};

const navItems = [
  { title: "Home", icon: DashboardIcon, path: "/dashboard" },
  ...retailModulesList,
];

const RetailSidebarItem = ({ collapsed, active, disabled, icon: Icon, label, onClick }) => {
  const theme = useTheme();
  const button = (
    <Button
      onClick={onClick}
      disabled={disabled}
      fullWidth
      sx={{
        borderRadius: 2,
        color: active ? "primary.main" : "text.secondary",
        justifyContent: collapsed ? "center" : "flex-start",
        minHeight: 44,
        minWidth: 0,
        px: collapsed ? 1 : 1.5,
        textTransform: "none",
        bgcolor: active ? alpha(theme.palette.primary.main, 0.1) : "transparent",
        opacity: disabled ? 0.45 : 1,
        "& .MuiButton-startIcon": {
          mr: collapsed ? 0 : 1,
        },
        "&:hover": {
          bgcolor: active
            ? alpha(theme.palette.primary.main, 0.14)
            : alpha(theme.palette.primary.main, 0.06),
          color: "primary.main",
        },
      }}
      startIcon={<Icon sx={{ fontSize: 20 }} />}
    >
      {!collapsed && (
        <Typography variant="body2" sx={{ fontWeight: active ? 800 : 600 }} noWrap>
          {label}
        </Typography>
      )}
    </Button>
  );

  return collapsed ? (
    <Tooltip title={disabled ? `${label} - FOR REVAMP` : label}>{button}</Tooltip>
  ) : (
    button
  );
};

const RetailSidebarLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = ls.get("user");
  const [collapsed, setCollapsed] = useState(getInitialCollapsed);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [unviewedCount, setUnviewedCount] = useState(0);
  const [isSyncingMaster, setIsSyncingMaster] = useState(false);
  const envMode =
    (process.env.REACT_APP_APP_MODE || process.env.REACT_APP_API_MODE || "")
      .toUpperCase()
      .trim();
  const isLocalAppMode = envMode.startsWith("LOCAL");

  useEffect(() => {
    window.localStorage.setItem(COLLAPSED_KEY, collapsed ? "true" : "false");
  }, [collapsed]);

  useEffect(() => {
    if (location.pathname.startsWith("/retail/")) {
      setCollapsed(true);
    }
  }, [location.pathname]);

  const activePath = useMemo(() => {
    const exact = navItems.find((item) => item.path === location.pathname);
    if (exact) return exact.path;

    const section = navItems.find(
      (item) => item.path !== "/dashboard" && location.pathname.startsWith(item.path)
    );
    return section?.path || "/dashboard";
  }, [location.pathname]);

  const handleLogout = async () => {
    const result = await fireSwal({
      title: "Are you sure?",
      text: "You will be logged out and redirected to the login page.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Logout!",
    });

    if (result.isConfirmed) {
      ls.clear();
      window.location.href = "/login";
    }
  };

  const handleForceMasterSync = async () => {
    if (!isLocalAppMode || isSyncingMaster) return;
    setIsSyncingMaster(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/local/master/refresh`
      );
      await fireSwal({
        icon: data?.skipped ? "info" : "success",
        title: data?.skipped ? "Master Sync Skipped" : "Master Sync Triggered",
        text: data?.reason || data?.message || "Master sync triggered.",
      });
    } catch (error) {
      await fireSwal({
        icon: "error",
        title: "Master Sync Failed",
        text: error?.response?.data?.message || error.message || "Master sync failed",
      });
    } finally {
      setIsSyncingMaster(false);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: "#f7f9fc",
        display: { xs: "block", md: "flex" },
        minHeight: "100vh",
      }}
    >
      <Paper
        component="aside"
        elevation={0}
        sx={{
          bgcolor: "#fff",
          borderRadius: 0,
          borderRight: { md: "1px solid" },
          borderBottom: { xs: "1px solid", md: 0 },
          borderColor: "divider",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          minHeight: { md: "100vh" },
          p: 1.5,
          position: { md: "sticky" },
          top: 0,
          transition: "width 0.18s ease",
          width: { xs: "100%", md: collapsed ? 84 : 280 },
          zIndex: 5,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <Box display="flex" alignItems="center" gap={1.25} minWidth={0}>
            <AccountCircle sx={{ color: "text.secondary", fontSize: 38 }} />
            {!collapsed && (
              <Box minWidth={0}>
                <Typography variant="overline" color="primary" sx={{ fontWeight: 800, lineHeight: 1 }}>
                  Retail POS
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.15 }} noWrap>
                  {user?.companyname || "Retail Store"}
                </Typography>
              </Box>
            )}
          </Box>
          <IconButton
            onClick={() => setCollapsed((prev) => !prev)}
            size="small"
            sx={{ display: { xs: "none", md: "inline-flex" } }}
          >
            {collapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Box>

        {!collapsed && (
          <Box
            sx={{
              bgcolor: alpha("#0958d7", 0.06),
              border: "1px solid",
              borderColor: alpha("#0958d7", 0.14),
              borderRadius: 2,
              p: 1.25,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              Signed in as
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.25 }} noWrap>
              {[user?.firstname, user?.lastname].filter(Boolean).join(" ") || "Current User"}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }} noWrap>
              {user?.email || "No email available"}
            </Typography>
          </Box>
        )}

        <Stack
          spacing={0.5}
          sx={{
            display: { xs: "grid", md: "flex" },
            gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))" },
          }}
        >
          {navItems.map((item) => (
            <RetailSidebarItem
              key={item.title}
              active={item.useRetailAlias ? location.pathname.startsWith(`${item.path}/`) : activePath === item.path}
              collapsed={collapsed}
              disabled={item.retailStatus === "revamp"}
              icon={item.icon}
              label={item.title}
              onClick={() => navigate(resolveRetailModulePath(item, user))}
            />
          ))}
        </Stack>

        <Box sx={{ display: { xs: "none", md: "block" }, flexGrow: 1 }} />

        <Stack spacing={1}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setInboxOpen(true)}
            sx={{ justifyContent: collapsed ? "center" : "center", minWidth: 0 }}
          >
            <Badge color="error" badgeContent={unviewedCount} invisible={!unviewedCount} sx={{ mr: collapsed ? 0 : 1 }}>
              <EmailOutlined fontSize="small" />
            </Badge>
            {!collapsed && "Inbox"}
          </Button>
          {isLocalAppMode && (
            <Button
              variant="contained"
              onClick={handleForceMasterSync}
              disabled={isSyncingMaster}
              sx={{ minWidth: 0 }}
            >
              {collapsed ? "Sync" : isSyncingMaster ? "Syncing..." : "Force Master Sync"}
            </Button>
          )}
          <Button
            variant="contained"
            color="secondary"
            onClick={handleLogout}
            sx={{ minWidth: 0 }}
          >
            {collapsed ? <Logout fontSize="small" /> : "Logout"}
          </Button>
        </Stack>
      </Paper>

      <Box component="main" sx={{ flex: 1, minWidth: 0 }}>
        {children}
      </Box>

      <Suspense fallback={null}>
        <DashboardNotif user={user} onUnviewedCountChange={setUnviewedCount} />
        {inboxOpen && (
          <DashboardInboxModal
            open={inboxOpen}
            onClose={() => setInboxOpen(false)}
            user={user}
          />
        )}
      </Suspense>
    </Box>
  );
};

export default RetailSidebarLayout;
